import { useMemo } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ACTIVITY_FILTER_TYPES, type ActivityType } from '@/types/activity'

interface ActivityTypeFilterProps {
  value: ActivityType[]
  onChange: (types: ActivityType[]) => void
  /**
   * Роли субъекта журнала (slug'и): у учителя нет «Урок пройден», у ученика —
   * «Урок проведён». Не переданы — показываем оба lesson-фильтра.
   */
  roles?: string[]
}

/**
 * Чипы фильтра по типам событий. Пустой выбор = все типы (так же трактует API).
 */
export function ActivityTypeFilter({ value, onChange, roles }: ActivityTypeFilterProps) {
  const { t } = useTranslation()

  const visibleTypes = useMemo(
    () =>
      ACTIVITY_FILTER_TYPES.filter((type) => {
        if (!roles) return true
        if (type === 'lesson.completed_as_teacher') return roles.includes('teacher')
        if (type === 'lesson.completed_as_student') return roles.includes('student')
        return true
      }),
    [roles],
  )

  const toggle = (type: ActivityType) => {
    onChange(value.includes(type) ? value.filter((item) => item !== type) : [...value, type])
  }

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {t('activity.filters')}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {visibleTypes.map((type) => (
          <Chip
            key={type}
            label={t(`activity.types.${type.replace(/\./g, '_')}`)}
            size="small"
            color={value.includes(type) ? 'primary' : 'default'}
            variant={value.includes(type) ? 'filled' : 'outlined'}
            onClick={() => toggle(type)}
          />
        ))}
        {value.length > 0 && (
          <Chip
            label={t('activity.resetFilters')}
            size="small"
            variant="outlined"
            onClick={() => onChange([])}
          />
        )}
      </Stack>
    </Box>
  )
}
